import theme from '../../styles/theme'
import { AdvertiserCostDetails, PublisherCostDetails } from '../../__generated__/graphql'
import { formatSessionTimeSeconds } from '../../utils/formatSessionTimeSeconds'

export interface StatStyle {
  color: string
  style: 'decimal' | 'currency' | 'percent' | 'duration'
  name: string
}

interface GraphStatStyle extends StatStyle {
  family: string
  isAxisVisible?: boolean
}

export interface BarStatStyle extends GraphStatStyle {
  display: 'bar'
  stackId: string
}

export interface LineStatStyle extends GraphStatStyle {
  display: 'line'
}

export type TimeseriesStatStyle = BarStatStyle | LineStatStyle

export const DefaultTimeseriesStatStyle: TimeseriesStatStyle = {
  color: theme.palette.grey[500],
  style: 'decimal',
  name: 'unknown',
  family: 'unknown',
  display: 'line',
}

export const getUnit = (statStyle: StatStyle): string => {
  switch (statStyle.style) {
    case 'percent':
      return '%'
    case 'currency':
      return '€'

    default:
      return ''
  }
}

export const valueFormatter = (statStyle: StatStyle, value: number): string => {
  if (statStyle.style === 'duration') return formatSessionTimeSeconds(value)

  return Intl.NumberFormat('fr-FR', {
    style: statStyle.style,
    maximumFractionDigits: 2,
    currency: 'EUR',
  }).format(statStyle.style === 'percent' ? value / 100 : value)
}

export type StatsConfKeys = keyof AdvertiserCostDetails | keyof PublisherCostDetails
type Stats = Record<StatsConfKeys, TimeseriesStatStyle>

export const StatsConf: Partial<Stats> = {
  displays: {
    color: theme.palette.secondary.light,
    style: 'decimal',
    name: 'displays',
    family: 'impression',
    display: 'line',
  },
  impressions: {
    color: theme.palette.secondary.main,
    style: 'decimal',
    name: 'impressions',
    family: 'impression',
    display: 'line',
  },
  clicks: {
    color: theme.palette.info.main,
    style: 'decimal',
    name: 'clicks',
    family: 'userEvent',
    display: 'line',
    isAxisVisible: false,
  },
  ctr: {
    color: theme.palette.success.main,
    style: 'percent',
    name: 'ctr',
    family: 'costRatio',
    display: 'line',
    isAxisVisible: false,
  },
  cpc: {
    color: '#ffde00',
    style: 'currency',
    name: 'cpc',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  rpm: {
    color: '#6EC0E2',
    style: 'currency',
    name: 'rpm',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  cpm: {
    color: '#F7915D',
    style: 'currency',
    name: 'cpm',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  commissions: {
    color: theme.palette.error.main,
    style: 'currency',
    name: 'commissions',
    family: 'commissions',
    display: 'line',
  },
  costs: {
    color: theme.palette.error.main,
    style: 'currency',
    name: 'costs',
    family: 'costs',
    display: 'line',
  },
  leads: {
    color: '#F7915D',
    style: 'decimal',
    name: 'leads',
    family: 'userEvent2',
    display: 'line',
    isAxisVisible: false,
  },
  orders: {
    color: '#8B0000',
    style: 'decimal',
    name: 'orders',
    family: 'userEvent2',
    display: 'line',
    isAxisVisible: false,
  },
  cpl: {
    color: '#000000',
    style: 'currency',
    name: 'cpl',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  cpo: {
    color: '#919191',
    style: 'currency',
    name: 'cpo',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  lr: {
    color: 'FFF',
    style: 'percent',
    name: 'LR%',
    family: 'costRatio',
    display: 'line',
    isAxisVisible: false,
  },
  or: {
    color: 'FFF',
    style: 'percent',
    name: 'OR',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  roi: {
    color: 'FFF',
    style: 'percent',
    name: 'roi',
    family: 'costStats',
    display: 'line',
    isAxisVisible: false,
  },
  revenue: {
    color: 'FFF',
    style: 'currency',
    name: 'revenue',
    family: 'costStats',
    display: 'bar',
    stackId: 'a',
  },
  pageViews: {
    color: '#7B68EE',
    style: 'decimal',
    name: 'pageViews',
    family: 'analytics',
    display: 'line',
  },
  bounceRate: {
    color: '#E74C3C',
    style: 'percent',
    name: 'bounceRate',
    family: 'analyticsRatio',
    display: 'line',
    isAxisVisible: false,
  },
  totalUsers: {
    color: '#3498DB',
    style: 'decimal',
    name: 'totalUsers',
    family: 'analytics',
    display: 'line',
  },
  newUsers: {
    color: '#1ABC9C',
    style: 'decimal',
    name: 'newUsers',
    family: 'analytics',
    display: 'line',
  },
  sessions: {
    color: '#9B59B6',
    style: 'decimal',
    name: 'sessions',
    family: 'analytics',
    display: 'line',
  },
  engagedSessions: {
    color: '#8E44AD',
    style: 'decimal',
    name: 'engagedSessions',
    family: 'analytics',
    display: 'line',
  },
  sessionTimeSeconds: {
    color: '#F39C12',
    style: 'duration',
    name: 'sessionTimeSeconds',
    family: 'analyticsDuration',
    display: 'line',
    isAxisVisible: false,
  },
  cpv: {
    color: '#16A085',
    style: 'currency',
    name: 'cpv',
    family: 'analyticsCost',
    display: 'line',
    isAxisVisible: false,
  },
  engagementRate: {
    color: '#27AE60',
    style: 'percent',
    name: 'engagementRate',
    family: 'analyticsRatio',
    display: 'line',
    isAxisVisible: false,
  },
}
